import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, Clock, Wrench, Phone, CheckCircle2, Shield, Users, FileText, Calendar } from 'lucide-react';
import { useScrollAnimations } from '../hooks/useScrollAnimations';

export const PropertyManagement = () => {
  useScrollAnimations();
  
  const services = [
    {
      icon: <Wrench size={28} />,
      title: 'Reactive Repairs',
      description: 'Fast response to tenant-reported issues. Doors, windows, locks, and glazing fixed right first time.'
    },
    {
      icon: <Calendar size={28} />,
      title: 'Planned Maintenance',
      description: 'Scheduled maintenance programmes to prevent issues before they become emergencies.'
    },
    {
      icon: <Shield size={28} />,
      title: 'Security Upgrades',
      description: 'Lock replacements, security assessments, and upgrades to meet insurance requirements.'
    },
    {
      icon: <Building2 size={28} />,
      title: 'Void Property Works',
      description: 'Get properties tenant-ready fast. Lock changes, repairs, and security checks between tenancies.'
    },
    {
      icon: <FileText size={28} />,
      title: 'Compliance Support',
      description: 'Documentation, reports, and audit trails for your property management records.'
    },
    {
      icon: <Users size={28} />,
      title: 'Tenant Liaison',
      description: 'Direct communication with tenants. We handle access arrangements and updates.'
    }
  ];

  const benefits = [
    {
      title: 'Single Point of Contact',
      description: 'One call handles all door, window, lock, and glazing issues across your entire portfolio.'
    },
    {
      title: 'Consistent Reporting',
      description: 'Standardised reports with photos, recommendations, and cost transparency for every job.'
    },
    {
      title: 'Flexible Scheduling',
      description: 'Work around tenant availability. Evening and weekend appointments available when needed.'
    },
    {
      title: 'Portfolio Pricing',
      description: 'Competitive rates for property management companies with multiple properties.'
    },
    {
      title: 'Emergency Response',
      description: '24/7 availability for genuine emergencies. Secure properties fast when needed.'
    },
    {
      title: 'Repair-First Approach',
      description: 'We fix rather than replace where possible. Save money while maintaining quality.'
    }
  ];

  const propertyTypes = [
    'Residential blocks',
    'HMOs',
    'Student accommodation',
    'Build-to-rent developments',
    'Social housing',
    'Commercial properties',
    'Mixed-use buildings',
    'Retirement living'
  ];

  return (
    <div>
      {/* Hero */}
      <section style={{
        background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
        padding: '80px 0 60px',
        color: 'var(--royal-white)'
      }}>
        <div className="royal-container">
          <div style={{
            display: 'inline-block',
            padding: '8px 16px',
            background: 'rgba(200, 168, 96, 0.2)',
            borderRadius: '6px',
            color: 'var(--royal-gold)',
            fontSize: '0.9rem',
            fontWeight: '600',
            marginBottom: '20px',
            border: '1px solid rgba(200, 168, 96, 0.3)'
          }}>
            For Property Professionals
          </div>
          <h1 className="royal-heading-xl" style={{ color: 'var(--royal-white)', marginBottom: '16px' }}>
            Property Maintenance & Management Companies
          </h1>
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: '600', 
            color: 'var(--royal-gold)', 
            marginBottom: '16px',
            fontStyle: 'italic'
          }}>
            Convenience as a Service
          </h2>
          <p style={{ fontSize: '1.25rem', maxWidth: '700px', color: 'rgba(255, 255, 255, 0.9)' }}>
            Reliable door, window, and lock specialists for your property portfolio. Reduce your subcontractor headaches with one trusted partner.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="royal-section" style={{ background: 'var(--royal-white)' }}>
        <div className="royal-container">
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 60px' }}>
            <h2 className="royal-heading-lg">Services for Property Managers</h2>
            <p className="royal-body-lg">
              From reactive repairs to planned maintenance programmes, we handle all your door, window, and lock requirements.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px'
          }}>
            {services.map((service, index) => (
              <div key={index} className="scroll-animate" style={{
                padding: '28px',
                background: 'var(--bg-section-alt)',
                borderRadius: '12px',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--royal-gold)',
                  marginBottom: '20px'
                }}>
                  {service.icon}
                </div>
                <h3 style={{ fontSize: '1.15rem', fontWeight: '600', color: 'var(--royal-navy)', marginBottom: '10px' }}>
                  {service.title}
                </h3>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Partner With Us */}
      <section className="royal-section" style={{ background: 'var(--bg-section-alt)' }}>
        <div className="royal-container">
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 60px' }}>
            <h2 className="royal-heading-lg">Why Partner With Royal Security?</h2>
            <p className="royal-body-lg">
              We understand the pressures of property management. Here's why maintenance companies choose us.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px'
          }}>
            {benefits.map((benefit, index) => (
              <div key={index} className="scroll-animate" style={{
                padding: '28px',
                background: 'var(--royal-white)',
                borderRadius: '12px',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <CheckCircle2 size={24} color="var(--royal-gold)" />
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: 'var(--royal-navy)', margin: 0 }}>
                    {benefit.title}
                  </h3>
                </div>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6', marginLeft: '36px' }}>
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Property Types */}
      <section className="royal-section" style={{ background: 'var(--royal-white)' }}>
        <div className="royal-container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '60px',
            alignItems: 'center'
          }}>
            <div>
              <h2 className="royal-heading-lg" style={{ marginBottom: '20px' }}>
                Property Types We Service
              </h2>
              <p className="royal-body-lg" style={{ marginBottom: '30px' }}>
                From single HMOs to large residential blocks, we have experience across the full range of managed properties.
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '12px'
              }}>
                {propertyTypes.map((type, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    color: 'var(--text-secondary)'
                  }}>
                    <CheckCircle2 size={18} color="var(--royal-gold)" />
                    <span>{type}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="scroll-animate" style={{
              background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
              padding: '40px',
              borderRadius: '16px',
              color: 'var(--royal-white)'
            }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '16px' }}>
                Portfolio Account Benefits
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
                  <CheckCircle2 size={20} color="var(--royal-gold)" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>Dedicated account manager for your portfolio</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
                  <CheckCircle2 size={20} color="var(--royal-gold)" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>Priority scheduling for urgent repairs</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
                  <CheckCircle2 size={20} color="var(--royal-gold)" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>Consolidated monthly invoicing option</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
                  <CheckCircle2 size={20} color="var(--royal-gold)" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>Volume-based pricing for regular work</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckCircle2 size={20} color="var(--royal-gold)" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>Quarterly review meetings available</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{
        background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
        padding: '80px 0',
        textAlign: 'center'
      }}>
        <div className="royal-container">
          <h2 style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--royal-white)', marginBottom: '16px' }}>
            Let's Discuss Your Portfolio
          </h2>
          <p style={{
            fontSize: '1.15rem',
            color: 'rgba(255, 255, 255, 0.9)',
            maxWidth: '600px',
            margin: '0 auto 32px'
          }}>
            Whether you manage 10 properties or 1,000, we can tailor a service agreement to suit your needs.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="royal-btn-primary" style={{ background: 'var(--royal-gold)', color: 'var(--royal-navy)' }}>
              Get in Touch
            </Link>
            <a href="tel:07710613783" className="royal-btn-light">
              Call 07710 613783
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
